/**
 * Live ledger board — joins the board's socket room and refetches when its
 * standings move.
 *
 * The push carries only the board id, never a standings slice: what a given
 * viewer may see (their own row, opt-in scoping, page window) is the read
 * route's decision, so the socket says "this moved" and the existing query
 * answers "here is your view of it". Same room lane competition leaderboards
 * already use — the backend validates any ObjectId, and a score config id is
 * one.
 */
import {useCallback, useContext, useEffect, useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {WebsocketEvent} from "@devour/client";
import {WebsocketProviderContext} from "@/components/WebsocketProvider";
import useWebsocket from "@/hooks/useWebsocket";

interface LedgerBoardUpdatePayload {
    ledgerBoardId?: string;
}

export function useJoinLedgerBoardRoom(ledgerBoardId: string | undefined, enabled = true): void {
    const queryClient = useQueryClient();
    const {socket, socketConnection} = useContext(WebsocketProviderContext);

    /*
     * The board this hook has already joined once. A SECOND join for the same
     * board is a reconnect, and a reconnect is the one moment we know a push may
     * have been missed.
     */
    const joinedBoardRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!socket || !socketConnection || !ledgerBoardId || !enabled) return;
        socket.emit("leaderboard:join", ledgerBoardId);

        /*
         * REPAIR ON REJOIN.
         *
         * A push is fire-and-forget: anything broadcast while this client was
         * disconnected is gone, and the 60s poll underneath is suppressed while
         * the document is hidden — which is exactly what a phone does the moment
         * the app goes to the background. A member earned points, the socket
         * dropped and came back, and the board still showed the old standings
         * until they closed and reopened the app.
         *
         * Only on a REJOIN: the first join happens as the queries are making
         * their own first request, and invalidating then would just duplicate it.
         */
        const isRejoin = joinedBoardRef.current === ledgerBoardId;
        joinedBoardRef.current = ledgerBoardId;
        if (isRejoin) {
            queryClient.invalidateQueries({queryKey: ["ledger-standings", ledgerBoardId]});
            queryClient.invalidateQueries({queryKey: ["ledger-my-contributions", ledgerBoardId]});
            /* Badges move on the same pass, so they were missed by the same frame. */
            queryClient.invalidateQueries({queryKey: ["drop-achievements"]});
        }
        return () => {
            socket.emit("leaderboard:leave", ledgerBoardId);
        };
    }, [socket, socketConnection, ledgerBoardId, enabled, queryClient]);

    const handleBoardUpdate = useCallback((payload: LedgerBoardUpdatePayload) => {
        /*
         * The room is per-board, but a socket can hold several rooms — check
         * the payload so one board's movement cannot refetch another's.
         */
        if (!enabled || !ledgerBoardId) return;
        if (payload?.ledgerBoardId && payload.ledgerBoardId !== ledgerBoardId) return;
        queryClient.invalidateQueries({queryKey: ["ledger-standings", ledgerBoardId]});
        queryClient.invalidateQueries({queryKey: ["ledger-my-contributions", ledgerBoardId]});
        /*
         * Badges move on the same pipeline pass the board does — a member
         * acting inside the partner app with the surface open behind it should
         * see the unlock now, not on their next load.
         */
        queryClient.invalidateQueries({queryKey: ["drop-achievements"]});
    }, [enabled, ledgerBoardId, queryClient]);

    /* The third argument is "login required", not "enabled" — the board is public, so signed-out viewers listen too. */
    useWebsocket<LedgerBoardUpdatePayload>(
        WebsocketEvent.SCORE_BOARD_UPDATE,
        handleBoardUpdate,
        false,
    );
}
